import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { PurchaseInvoiceDetailLink } from "@/components/hub/HubEntityDetailLink";
import { Badge, type BadgeTone } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import type { SortDirection } from "@/components/ui/SortableTableHeader";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { ApiError } from "@/lib/api";
import { usePageBreadcrumb } from "@/lib/navigationTrailContext";
import {
  EARLY_PAYMENT_STATUS_LABELS,
  earlyPaymentFiltersActive,
  earlyPaymentRequesterLabel,
  filterEarlyPaymentRequests,
  sortEarlyPaymentRequests,
  type EarlyPaymentSortKey,
  type EarlyPaymentStatusFilter,
} from "./earlyPaymentRequestList";
import {
  fetchEarlyPaymentRequests,
  updateEarlyPaymentRequestStatus,
  type EarlyPaymentRequestRow,
  type EarlyPaymentRequestStatus,
} from "./earlyPaymentRequestsApi";

const STATUS_FILTERS: Array<{ value: EarlyPaymentStatusFilter; label: string }> = [
  { value: "all", label: "All" },
  { value: "requested", label: "Requested" },
  { value: "paid", label: "Paid" },
  { value: "cancelled", label: "Cancelled" },
];

function formatChicagoDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Chicago",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function amountLabel(row: EarlyPaymentRequestRow): string {
  const amount = row.total_amount?.trim() ?? "";
  if (amount === "") {
    return "";
  }
  const currency = row.currency?.trim() ?? "";
  return currency !== "" ? `${amount} ${currency}` : amount;
}

function gipiId(row: EarlyPaymentRequestRow): number | null {
  const raw = row.purchase_invoice_id?.trim() ?? "";
  if (raw === "") {
    return null;
  }
  const parsed = Number(raw);
  if (!Number.isInteger(parsed) || parsed <= 0) {
    return null;
  }
  return parsed;
}

function statusTone(status: EarlyPaymentRequestStatus): BadgeTone {
  if (status === "paid") {
    return "success";
  }
  if (status === "cancelled") {
    return "muted";
  }
  return "warning";
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "The early payment requests could not be loaded.";
}

type EarlyPaymentUpdateMutation = {
  isPending: boolean;
  variables?: { id: string; status: "paid" | "cancelled" };
  mutate: (input: { id: string; status: "paid" | "cancelled" }) => void;
};

function earlyPaymentColumns(
  updateMutation: EarlyPaymentUpdateMutation,
): HubDataTableColumn<EarlyPaymentRequestRow, EarlyPaymentSortKey>[] {
  return [
    {
      id: "requested_at",
      header: "Requested",
      sortKey: "requested_at",
      overflow: "nowrap",
      sortValue: (row) => row.requested_at,
      cell: (row) => formatChicagoDateTime(row.requested_at),
    },
    {
      id: "requested_by",
      header: "Asked by",
      sortKey: "requested_by",
      sortValue: (row) => earlyPaymentRequesterLabel(row),
      cell: (row) => earlyPaymentRequesterLabel(row),
    },
    {
      id: "vendor_invoice_number",
      header: "Vendor invoice",
      sortKey: "vendor_invoice_number",
      sortValue: (row) => row.vendor_invoice_number,
      cell: (row) => (
        <Link
          className="font-medium text-sky-700 hover:text-sky-900 hover:underline"
          to={`/accounting/ap/${encodeURIComponent(row.accounts_payable_invoice_id)}/edit`}
        >
          {row.vendor_invoice_number}
        </Link>
      ),
    },
    {
      id: "supplier_name",
      header: "Vendor",
      sortKey: "supplier_name",
      sortValue: (row) => row.supplier_name?.trim() ?? "",
      cell: (row) => row.supplier_name?.trim() || "",
    },
    {
      id: "total_amount",
      header: "Amount",
      sortKey: "total_amount",
      align: "right",
      overflow: "nowrap",
      sortValue: (row) => row.total_amount?.trim() ?? "",
      cell: (row) => amountLabel(row),
    },
    {
      id: "gipi_number",
      header: "GIPI",
      sortKey: "gipi_number",
      sortValue: (row) => row.gipi_number?.trim() ?? "",
      cell: (row) => {
        const linkedGipiId = gipiId(row);
        return linkedGipiId != null ? (
          <PurchaseInvoiceDetailLink
            purchaseInvoiceId={linkedGipiId}
            mvNumber={row.gipi_number}
            vendorInvoiceNumber={row.vendor_invoice_number}
          />
        ) : (
          <span className="text-slate-600">Not in yet</span>
        );
      },
    },
    {
      id: "status",
      header: "Status",
      sortKey: "status",
      sortValue: (row) => row.status,
      cell: (row) => <Badge tone={statusTone(row.status)}>{EARLY_PAYMENT_STATUS_LABELS[row.status]}</Badge>,
    },
    {
      id: "update",
      header: "Update",
      sortValue: () => "",
      cell: (row) => {
        if (row.status !== "requested") {
          return null;
        }
        const pending = updateMutation.isPending && updateMutation.variables?.id === row.id;
        return (
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              disabled={pending}
              isLoading={pending && updateMutation.variables?.status === "paid"}
              onClick={(event) => {
                event.stopPropagation();
                updateMutation.mutate({ id: row.id, status: "paid" });
              }}
            >
              Mark paid
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={pending}
              onClick={(event) => {
                event.stopPropagation();
                updateMutation.mutate({ id: row.id, status: "cancelled" });
              }}
            >
              Cancel request
            </Button>
          </div>
        );
      },
    },
  ];
}

export function EarlyPaymentRequestsPage(): JSX.Element {
  usePageBreadcrumb("Early payment requests");
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get("request")?.trim() ?? "";
  const [status, setStatus] = useState<EarlyPaymentStatusFilter>("all");
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<EarlyPaymentSortKey>("requested_at");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const listQuery = useQuery({
    queryKey: ["accounting", "early-payment-requests"],
    queryFn: fetchEarlyPaymentRequests,
  });
  const updateMutation = useMutation({
    mutationFn: (input: { id: string; status: "paid" | "cancelled" }) =>
      updateEarlyPaymentRequestStatus(input.id, input.status),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["accounting", "early-payment-requests"] });
    },
  });

  const rows = listQuery.data?.rows ?? [];
  const filtersActive = earlyPaymentFiltersActive(status, search);
  const visibleRows = useMemo(
    () => sortEarlyPaymentRequests(filterEarlyPaymentRequests(rows, status, search), sortKey, sortDirection),
    [rows, search, sortDirection, sortKey, status],
  );

  function toggleSort(key: EarlyPaymentSortKey): void {
    if (key === sortKey) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDirection("asc");
  }

  const columns = useMemo(() => earlyPaymentColumns(updateMutation), [updateMutation]);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <PageTitleWithHelp hoverHint="Requests to pay a vendor invoice promptly, including when the Megaventory GIPI is not in yet. Marking a request paid records it here. It does not create a QuickBooks bill or email the vendor.">
        Early payment requests
      </PageTitleWithHelp>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody className="grid gap-3 md:grid-cols-2">
          <div className="grid gap-1">
            <Label htmlFor="early-payment-search">Search early payment requests</Label>
            <Input
              id="early-payment-search"
              type="search"
              value={search}
              placeholder="Filter by invoice, vendor, or who asked"
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <div className="grid gap-1">
            <Label>Status</Label>
            <div className="flex flex-wrap gap-2">
              {STATUS_FILTERS.map((option) => (
                <Button
                  key={option.value}
                  type="button"
                  size="sm"
                  variant={status === option.value ? "primary" : "secondary"}
                  aria-pressed={status === option.value}
                  onClick={() => setStatus(option.value)}
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </div>
          {filtersActive ? (
            <p className="text-sm text-slate-600 md:col-span-2">
              Showing {visibleRows.length} of {rows.length} early payment requests
            </p>
          ) : null}
        </CardBody>
      </Card>

      {updateMutation.error != null ? (
        <p className="text-sm text-rose-700">{errorMessage(updateMutation.error)}</p>
      ) : null}

      <HubDataTable
        title={rows.length === 1 ? "1 early payment request" : `${String(rows.length)} early payment requests`}
        columns={columns}
        rows={visibleRows}
        rowKey={(row) => row.id}
        sortKey={sortKey}
        sortDir={sortDirection}
        onSort={toggleSort}
        loading={listQuery.isLoading}
        loadingMessage="Loading early payment requests…"
        error={listQuery.error != null ? errorMessage(listQuery.error) : null}
        empty="No early payment requests yet."
        isFiltered={filtersActive}
        filteredEmpty="No early payment requests match these filters."
        rowClassName={(row) => (highlightId === row.id ? "bg-amber-50" : undefined)}
      />
    </div>
  );
}
